import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AuthLayout } from "@/components/app/AuthLayout";
import { Field, Spinner } from "@/components/app/kit";
import { PasswordPair } from "@/pages/auth/ResetPassword";
import { api, formatApiError } from "@/lib/api";
import { useAuth, homeFor } from "@/context/AuthContext";
import { t } from "@/lib/i18n";

export default function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setMe } = useAuth();
  const [info, setInfo] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [f, setF] = useState({ name: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/invitation/${token}`)
      .then((r) => {
        setInfo(r.data);
        setF((x) => ({ ...x, name: r.data.name || "" }));
      })
      .catch((e) => setLoadError(formatApiError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password !== f.confirm) return setError("Les mots de passe ne correspondent pas");
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/invitation/accept", { token, password: f.password, name: f.name });
      setMe(data);
      toast.success("Compte activé. Bienvenue !");
      navigate(homeFor(data), { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (loadError)
    return (
      <AuthLayout title="Invitation indisponible">
        <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700 flex gap-3" data-testid="invitation-error">
          <ShieldAlert className="h-5 w-5 shrink-0" /> {loadError}
        </div>
        <Link to="/connexion" className="block mt-6 text-sm text-sky-700 hover:underline">Retour à la connexion</Link>
      </AuthLayout>
    );
  if (!info) return <div className="min-h-screen flex items-center justify-center"><Spinner /></div>;

  const where = info.tenant_name ? `le cabinet « ${info.tenant_name} »` : "l'administration de la plateforme";
  return (
    <AuthLayout title="Activez votre compte" subtitle={`Vous êtes invité(e) à rejoindre ${where}.`}>
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 mb-6 text-sm space-y-1" data-testid="invitation-info">
        <p><span className="text-slate-500">E-mail :</span> <strong>{info.email}</strong></p>
        <p><span className="text-slate-500">Rôle :</span> {info.role_name || t(`role.${info.platform_role}`)}</p>
      </div>
      <form onSubmit={submit} className="space-y-5">
        <Field label="Nom complet" required>
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="invitation-name-input" />
        </Field>
        <PasswordPair f={f} setF={setF} prefix="invitation" />
        {error && <p className="text-sm text-red-600" data-testid="invitation-submit-error">{error}</p>}
        <Button type="submit" className="w-full h-11" disabled={busy} data-testid="invitation-submit-button">
          {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Activer mon compte
        </Button>
      </form>
    </AuthLayout>
  );
}
